import React, { useState, useRef, useEffect, useCallback, useMemo, FormEvent } from 'react';
import { CalendarChangeParams } from 'primereact/calendar';
import moment from 'moment';
import SarParser from './SarParser';

export interface UseDateFieldReturn {
  calendarVisible: boolean;
  dateValue: Date | null;
  calendarRef: React.RefObject<HTMLDivElement>;
  handleDateChange: (e: CalendarChangeParams) => void;
  handleDateInputClick: () => void;
  handleDateInputChange: (event: FormEvent<HTMLInputElement>) => void;
}


const formatDateToString = (date: Date | null): string => {
  if (!date) {
    return '';
  }
  return moment(date).format('DD/MM/YYYY');
};

const useDateField = (value: string, onChange: (value: string) => void): UseDateFieldReturn => {
  const [calendarVisible, setCalendarVisible] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const handleDateChange = useCallback(
    (e: CalendarChangeParams): void => {
      const selectedDate = e.value as Date | null;
      const formattedDate = formatDateToString(selectedDate);
      onChange(formattedDate);
      setCalendarVisible(false);
    },
    [onChange],
  );

  const handleDateInputClick = useCallback((): void => {
    setCalendarVisible((prev) => !prev);
  }, []);

  const handleDateInputChange = useCallback(
    (event: FormEvent<HTMLInputElement>): void => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const dateValue = useMemo(() => {
    return SarParser.parseDateString(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setCalendarVisible(false);
      }
    };

    if (calendarVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [calendarVisible]);

  return {
    calendarVisible,
    dateValue,
    calendarRef,
    handleDateChange,
    handleDateInputClick,
    handleDateInputChange,
  };
};

export default useDateField;

